/**
 * Assertions Management
 * 
 * Assertions management
 * Features:
 * - Table view with all assertions
 * - Add/Edit/Delete via side panel
 * - API integration
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Table, Dropdown, message, Spin, Typography } from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import { MoreOutlined, EditOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import ArrowUpIcon from '../../assets/icons/ic-arrow-up.svg?react';
import AddItemButton from '../../components/common/AddItemButton';
import ConfirmModal from '../../components/common/ConfirmModal';
import trashIcon from '../../assets/icons/ic-trash.svg';
import AssertionItemDrawer, { 
  type AssertionFormData, 
} from './AssertionItemDrawer';
import {
  getAssertions,
  createAssertion,
  updateAssertion,
  deleteAssertion as deleteAssertionApi,
  AssertionBackend
} from '../../services/riskAssessmentService';

// ========== TYPE DEFINITIONS ==========
const { Title } = Typography;

// ========== STYLED COMPONENTS ==========

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TableWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 2px;

  .ant-table {
    border-radius: 4px;
    overflow: hidden;
    table-layout: fixed;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    color: #6B7280;
    padding: 12px 16px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    
    &:first-child {
      border-left: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 14px;
    color: #1F2937;
    padding: 16px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    overflow: hidden;
    
    &:first-child {
      border-left: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;


const NameChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: 20px;
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #1E40AF;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #DBEAFE;
  }
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

const MenuItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #1F2937;
`;

const MenuItemTextDanger = styled(MenuItemText)`
  color: #DC2626;
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 24px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: white;
  border-radius: 6px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;

// ========== PROPS ==========

interface AssertionsTabProps {

}

// ========== COMPONENT ==========

const AssertionsTab: React.FC<AssertionsTabProps> = () => {
  // Hooks
  const dispatch = useDispatch<AppDispatch>();
  
  // Redux state

  const { isExternalUser } = useSelector((state: RootState) => state.auth);
  
  // Get assertions from Redux, resolve empty control names from controlActivities store
  const [assertions, setAssertions ] = useState<AssertionBackend[]>([])
  
  // Local UI state
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [editingApp, setEditingApp] = useState<AssertionBackend | null>(null);
  const [insertAtIndex, setInsertAtIndex] = useState<number | null>(null);
  
  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<AssertionBackend | null>(null);
  const [deleting, setDeleting] = useState(false);


  // Fetch applications
  const fetchAssertions = useCallback(async () => {
   
    setLoading(true);
    try {
      const response = await getAssertions();
      
      // Dispatch to Redux - data is already mapped and sorted by Index in the service
      setAssertions(response.data);
    } catch (error: any) {
      console.error('Failed to fetch assertions:', error);
      message.error(error?.message || 'Failed to load assertions');
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  // Fetch assertions
  useEffect(() => {
    fetchAssertions();
  }, [fetchAssertions]);

  // Open drawer for adding new application
  const openAddDrawer = useCallback((atIndex?: number) => {
    setDrawerMode('add');
    setEditingApp(null);
    setInsertAtIndex(atIndex ?? null);
    setDrawerOpen(true);
  }, []);

  // Open drawer for editing
  const openEditDrawer = useCallback((app: AssertionBackend) => {
    setDrawerMode('edit');
    setEditingApp(app);
    setInsertAtIndex(null);
    setDrawerOpen(true);
  }, []);

  // Handle drawer submit
  const handleDrawerSubmit = useCallback(async (formData: AssertionFormData) => {
    
    try {
      if (drawerMode === 'add') {
        await createAssertion({
          assertionName: formData.name,

        });

        message.success('Assertion created successfully');
      } else if (drawerMode === 'edit' && editingApp) {
        await updateAssertion({
          assertionId: editingApp.assertionId,
          assertionName: formData.name,
        });

        message.success('Assertion updated successfully');
      }

      // Re-fetch to update Redux
      await fetchAssertions();

      setDrawerOpen(false);
      setEditingApp(null);
      setInsertAtIndex(null);
    } catch (error: any) {
      console.error('Failed to save application:', error);
      message.error(error?.message || 'Failed to save application');
    }
  }, [drawerMode, editingApp, insertAtIndex, assertions.length, fetchAssertions]);

  // Open delete modal
  const openDeleteModal = useCallback((app: AssertionBackend) => {
    setAppToDelete(app);
    setDeleteModalOpen(true);
  }, []);

  // Handle delete confirm
  const handleDeleteConfirm = useCallback(async () => {
    if (!appToDelete) return;

    setDeleting(true);
    try {
      await deleteAssertionApi(appToDelete.assertionId);
      
      message.success('Assertion deleted successfully');
      
      // Re-fetch to update Redux
      await fetchAssertions();
      
      setDeleteModalOpen(false);
      setAppToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete assertion:', error);
      message.error(error?.message || 'Failed to delete assertion');
    } finally {
      setDeleting(false);
    }
  }, [appToDelete, fetchAssertions]);

  // Get menu items for row actions
  const getMenuItems = (app: AssertionBackend): MenuProps['items'] => [
    {
      key: 'add',
      label: (
        <MenuItemContent>
          <ArrowUpIcon />
          <MenuItemText>Add New</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => openAddDrawer(0),
    },
    
    {
      key: 'edit',
      label: (
        <MenuItemContent>
          <EditOutlined />
          <MenuItemText>Edit</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => openEditDrawer(app),
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      label: (
        <MenuItemContent>
          <DeleteOutlined style={{ color: '#DC2626' }} />
          <MenuItemTextDanger>Delete</MenuItemTextDanger>
        </MenuItemContent>
      ),
      onClick: () => openDeleteModal(app),
    },
  ];


  // Table columns
  const columns: TableColumnsType<AssertionBackend> = [
    
    {
      title: 'Name',
      dataIndex: 'assertionName',
      key: 'assertionName',
      width: 500,
      render: (name: string, record: AssertionBackend) => (
        <NameChip onClick={() => openEditDrawer(record)}>
          {name}
        </NameChip>
      ),
    },
   
    {
      title: 'Actions',
      key: 'actions',
      width: 5,
      align: 'center',
      render: (_: unknown, record: AssertionBackend) => (
        <Dropdown
          menu={{ items: getMenuItems(record) }}
          trigger={['click']}
          placement="bottomRight"
        >
          <ActionButton onClick={(e) => e.stopPropagation()}>
            <MoreOutlined />
          </ActionButton>
        </Dropdown>
      ),
    },
  ];

  // Convert Application to ApplicationFormData
  const getFormData = (app: AssertionBackend | null): AssertionFormData | null => {
    if (!app) return null;
    return {
      id: app.assertionId,
      name: app.assertionName,
    };
  };
  
  // Get existing application names for duplicate validation
  const existingNames = assertions.map(app => app.assertionName);


  return (
    
    <Container>
        <PageHeader>
          <PageTitle level={2}>Assertions Management</PageTitle>
        </PageHeader>
      {/* Table */}
      {loading ? (
        <LoadingWrapper>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
        </LoadingWrapper>
      ) : assertions.length > 0 ? (
        <TableWrapper>
          <Table
            dataSource={assertions}
            columns={!isExternalUser ? columns : columns.filter(col => col.title != 'Actions')}
            rowKey="id"
            pagination={false}
            size="middle"
          />
        </TableWrapper>
      ) : (
        <EmptyStateWrapper>
          <EmptyStateText>There are no assertions to display yet.</EmptyStateText>
          <AddItemButton
            text="Add Assertion"
            onClick={() => openAddDrawer()}
          />
        </EmptyStateWrapper>
      )}

      {/* Application Drawer */}
      <AssertionItemDrawer
        open={drawerOpen}
        mode={drawerMode}
        initialValues={getFormData(editingApp)}
        existingNames={existingNames}
        onClose={() => {
          setDrawerOpen(false);
          setEditingApp(null);
          setInsertAtIndex(null);
        }}
        onSubmit={handleDrawerSubmit}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setAppToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        icon={trashIcon}
        title="Delete Assertion"
        description={
          <>
            Are you sure you want to delete <strong>{appToDelete?.assertionName}</strong>?
            <br /><br />
            This action cannot be undone.
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={deleting}
      />

    </Container>
  );
};

export default AssertionsTab;

